// src/prototype/components/EAJourneyBar.tsx
// ============================================================
// EA PROTOTYPE JOURNEY BAR v9
// UPDATED: Added HelpPopover icons to each journey step
// UPDATED: Uses isPro prop for plan-based feature gating
// UPDATED: Step 2 shows a freemium drill-in cap badge, derived from FREEMIUM_EXPLORER_LIMIT
// UPDATED: Step 3 locked for freemium, unlocked for pro
// 
// 3-Step Flow:
//   1. Personalize → Your Inputs (all users)
//   2. Evaluate → Review & Select Finalists (freemium: top-N use cases, pro: all)
//   3. Execute → Champion & Initiate (pro only)
// ============================================================

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Sliders, 
  Lightbulb, 
  Rocket, 
  CheckCircle2, 
  Lock 
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { EAPersonalizationWidget } from './EAPersonalizationWidget';
import { useCoverage } from '../hooks/useCoverage';
import { personalizationPercent, personalizationBand } from '../../lib/coverage/personalizationPercent';
import { useEntitlements } from '../hooks/useEntitlements';
import { HelpPopover } from './help/HelpPopover';
import type { HelpKey } from '../help/helpContent';

interface EAJourneyBarProps {
  onStepClick?: (stepId: number) => void;
  /** @deprecated - Now uses useDemoPlan hook directly */
  isPro?: boolean;
  /**
   * Optional shell-level override of which step reads as active. Leave undefined for the
   * default route-derived behavior (unchanged). Pass a step id to force it, or `null` to
   * render a neutral, present-but-inactive bar (no step highlighted/pulsing) — used when a
   * non-journey surface (e.g. an Enterprise tab) is active. Steps stay fully clickable
   * regardless.
   */
  activeStepOverride?: number | null;
  /**
   * Copy for the context row (Row B) when the bar is neutral (activeStepOverride = null).
   * Same two-part shape as a step description (bold thesis + explanatory sentence) so a
   * non-journey surface fills that row identically. When omitted on a neutral bar, Row B is
   * hidden. Ignored when a step is active. Optional rightNote fills the right-hand slot the
   * storefront uses for its per-step reinforcement line.
   */
  neutralDescription?: { thesis: string; description: string; rightNote?: string };
  /**
   * One thin row (h-11): inline step labels + a personalization chip, no description text.
   * Opt-in; defaults to false so every existing caller renders unchanged.
   */
  compact?: boolean;
}

// Step-specific descriptions - Updated for 3-step flow
const EA_STEP_DESCRIPTIONS: Record<string, { thesis: string; explanation: string }> = {
  personalize: {
    thesis: "Personalization shapes the intelligence you see.",
    explanation: "Your organizational inputs directly influence which opportunities surface, how they're evaluated, and how defensible the results are.",
  },
  evaluate: {
    thesis: "Evaluate opportunities and select your finalists.",
    explanation: "Review why each opportunity was identified, compare ROI projections, flag assumptions, and mark your top choices for approval.",
  },
  approve: {
    thesis: "Champion your initiatives and initiate execution.",
    explanation: "Access approval-ready materials, prioritize your initiatives, and prepare to defend your recommendations to leadership.",
  },
};

// Right-side reinforcement text per step
const EA_REINFORCEMENT_TEXT: Record<string, string> = {
  personalize: "Improving personalization increases relevance and confidence.",
  evaluate: "Your flags help us refine future projections.",
  approve: "Your execution kits are ready for leadership review.",
};

const eaSteps = [
  { 
    id: 1, 
    label: 'Personalize',
    sublabel: 'Your Inputs',
    icon: Sliders,
    route: '/home',
    tier: 'all', // Available to all users
    descriptionKey: 'personalize',
    helpKey: 'journey_personalize' as HelpKey,
  },
  { 
    id: 2, 
    label: 'Evaluate',
    sublabel: 'Review & Select Finalists',
    icon: Lightbulb,
    route: '/opportunities',
    tier: 'limited', // Freemium: the top-N drill-in cap applies; Pro: all
    descriptionKey: 'evaluate',
    helpKey: 'journey_evaluate_shortlist' as HelpKey,
  },
  { 
    id: 3, 
    label: 'Execute',
    sublabel: 'Champion & Initiate',
    icon: Rocket,
    route: '/activate',
    tier: 'pro', // Pro only
    descriptionKey: 'approve',
    helpKey: 'journey_approval_execute' as HelpKey,
  },
];

export function EAJourneyBar({ onStepClick, activeStepOverride, neutralDescription, compact = false }: EAJourneyBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);

  // The percentage every journey surface shows, derived on every render from the uploads
  // store by tier weight. Nothing here is cached or stored, so the number cannot drift
  // between two pages the way the old completion vector did.
  const { aggregates } = useCoverage();
  const personalizationPct = personalizationPercent(aggregates);

  // Use the unified demo plan hook directly - this is the source of truth
  // freemiumExplorerLimit is THE cap the Explorer actually enforces
  // (canViewOpportunityDetail gates on it). The badge used to read a hardcoded
  // "5 use cases" while the gate allowed 3 — the badge promised two more than the
  // product delivered. Deriving it means the two can never disagree again.
  const { isPro, freemiumExplorerLimit } = useEntitlements();
  
  console.log('🎯 EAJourneyBar - isPro:', isPro, 'path:', location.pathname);

  // Determine current step from route (updated for 3-step flow)
  // '/opportunity/' (singular, trailing slash) matches the use-case drilldown family
  // without also matching the '/opportunities' list route.
  const getCurrentStep = (): number => {
    const path = location.pathname;
    // Step 3: Get Approval & Execute (includes the initiative command center)
    if (path.includes('/activate') || path.includes('/execute/')) return 3;
    // Step 2: Evaluate & Shortlist (includes the use-case drilldown family)
    if (path.includes('/opportunities') || path.includes('/opportunity/')) return 2;
    // Upgrade page shows as between step 2 and 3
    if (path.includes('/upgrade')) return 2.5;
    // Default: Step 1 Personalize
    return 1;
  };

  // Shell may override the active step: undefined → route-derived (default, unchanged);
  // null → no active step (neutral bar, sentinel 0 matches no step id); a number → forced.
  const noActiveStep = activeStepOverride === null;
  const currentStep = activeStepOverride === undefined ? getCurrentStep() : (activeStepOverride ?? 0);

  // Get current step's description key
  const getCurrentDescriptionKey = (): string => {
    const path = location.pathname;
    if (path.includes('/activate') || path.includes('/execute/')) return 'approve';
    if (path.includes('/opportunities') || path.includes('/opportunity/')) return 'evaluate';
    if (path.includes('/upgrade')) return 'evaluate';
    return 'personalize';
  };

  const currentDescriptionKey = getCurrentDescriptionKey();
  const currentDescription = EA_STEP_DESCRIPTIONS[currentDescriptionKey];
  const currentReinforcement = EA_REINFORCEMENT_TEXT[currentDescriptionKey];

  const handleStepClick = (step: typeof eaSteps[0]) => {
    console.log('🚀 EAJourneyBar handleStepClick:', {
      stepId: step.id,
      stepLabel: step.label,
      stepTier: step.tier,
      stepRoute: step.route,
      isPro,
      willRedirectToUpgrade: step.tier === 'pro' && !isPro,
    });
    
    // Pro-only steps redirect to upgrade for freemium users
    if (step.tier === 'pro' && !isPro) {
      console.log('🔒 Redirecting to upgrade page because isPro is false');
      navigate('/upgrade');
      return;
    }

    console.log('✅ Navigating to:', step.route);
    if (onStepClick) {
      onStepClick(step.id);
    }
    navigate(step.route);
  };

  const isStepAccessible = (step: typeof eaSteps[0]): boolean => {
    if (step.tier === 'all') return true;
    if (step.tier === 'limited') return true; // Accessible but limited for freemium
    if (step.tier === 'pro') return isPro;
    return true;
  };

  const isStepCompleted = (stepId: number): boolean => {
    return stepId < Math.floor(currentStep);
  };

  const isStepLocked = (step: typeof eaSteps[0]): boolean => {
    return step.tier === 'pro' && !isPro;
  };

  if (compact) {
    const band = personalizationBand(personalizationPct);
    return (
      <div className="flex items-center gap-4 h-11 px-4 bg-white rounded-xl shadow-sm border border-[#e5e7eb]">
        <div className="flex items-center gap-1.5 min-w-0">
          {eaSteps.map((step, index) => {
            const StepIcon = step.icon;
            const isCurrent = Math.floor(currentStep) === step.id;
            const isCompleted = isStepCompleted(step.id);
            const isLocked = isStepLocked(step);
            return (
              <React.Fragment key={step.id}>
                <button
                  type="button"
                  onClick={() => handleStepClick(step)}
                  aria-label={`${step.label} - ${step.sublabel}${isLocked ? ' (Pro feature, locked)' : ''}`}
                  aria-current={isCurrent ? 'step' : undefined}
                  className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-colors ${
                    isCurrent
                      ? 'bg-[#FF6E4C] text-white font-bold'
                      : isLocked
                      ? 'text-[#717182] font-semibold hover:bg-[#F5F7FA]'
                      : 'text-[#18365E] font-semibold hover:bg-[#F5F7FA]'
                  }`}
                >
                  {isCompleted ? <CheckCircle2 className="w-3.5 h-3.5" /> : isLocked ? <Lock className="w-3.5 h-3.5" /> : <StepIcon className="w-3.5 h-3.5" />}
                  <span>{step.label}</span>
                </button>
                {index < eaSteps.length - 1 && (
                  <div className={`h-0.5 w-5 shrink-0 ${isCompleted ? 'bg-[#18365E]' : 'bg-[#e5e7eb]'}`} aria-hidden="true" />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => navigate('/home')}
          aria-label="Open your inputs"
          className="ml-auto shrink-0 flex items-center gap-1.5 rounded-full border border-[#e5e7eb] px-2.5 py-1 text-xs hover:border-[#18365E] transition-colors"
        >
          <span className="font-bold text-[#18365E] tabular-nums">{personalizationPct}%</span>
          <span className="text-[#717182]" aria-hidden="true">·</span>
          <span className={`font-semibold ${band.color}`}>{band.label}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-stretch gap-4">
      {/* Main Journey Bar */}
      <div className="flex-1 bg-white rounded-xl shadow-md border border-[#e5e7eb]">
        {/* ROW A: Stepper */}
        <div className="px-6 pt-3 pb-2">
          <div className="flex items-center justify-between">
            <TooltipProvider delayDuration={100}>
              {eaSteps.map((step, index) => {
                const StepIcon = step.icon;
                const isAccessible = isStepAccessible(step);
                const isCompleted = isStepCompleted(step.id);
                const isCurrent = Math.floor(currentStep) === step.id;
                const isLocked = isStepLocked(step);
                const isLimitedForFreemium = step.tier === 'limited' && !isPro;
                
                return (
                  <React.Fragment key={step.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div 
                          className={`flex flex-col items-center flex-1 group ${
                            isAccessible ? 'cursor-pointer' : 'cursor-not-allowed'
                          }`}
                          onMouseEnter={() => setHoveredStep(step.id)}
                          onMouseLeave={() => setHoveredStep(null)}
                          onClick={() => handleStepClick(step)}
                          role="button"
                          aria-label={`${step.label} - ${step.sublabel}${isLocked ? ' (Pro feature, locked)' : ''}`}
                        >
                          <div className="relative">
                            {/* Step circle */}
                            <div
                              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                                isCompleted
                                  ? 'bg-[#18365E] text-white'
                                  : isCurrent
                                  ? 'bg-[#FF6E4C] text-white shadow-lg'
                                  : isLocked
                                  ? 'bg-[#F5F7FA] text-[#717182] border-2 border-[#e5e7eb]'
                                  : 'bg-white text-[#18365E] border-2 border-[#18365E]/40'
                              } ${hoveredStep === step.id && isAccessible ? 'scale-110 shadow-xl' : ''}`}
                            >
                              {isCompleted ? (
                                <CheckCircle2 className="w-4 h-4" />
                              ) : isLocked ? (
                                <Lock className="w-3.5 h-3.5" />
                              ) : (
                                <StepIcon className="w-4 h-4" />
                              )}
                            </div>
                            
                            {/* Active step glow */}
                            {isCurrent && (
                              <div className="absolute inset-0 rounded-full bg-[#FF6E4C]/30 animate-ping" />
                            )}
                            
                            {/* Pro badge for Step 3 (locked state) */}
                            {isLocked && (
                              <div className="absolute -top-1 -right-1 px-1 py-0.5 rounded text-[7px] font-bold bg-[#e5e7eb] text-[#717182]">
                                PRO
                              </div>
                            )}
                            
                            {/* Drill-in cap badge. Shown only where a cap actually
                                binds: isLimitedForFreemium is false for pro and
                                enterprise (both resolve isPro true), so those tiers
                                fall through to no badge without a special case. */}
                            {isLimitedForFreemium && !isLocked && (
                              <div className="absolute -top-1 -right-2 px-1.5 py-0.5 rounded text-[7px] font-bold bg-amber-100 text-amber-700 whitespace-nowrap">
                                {freemiumExplorerLimit} use cases
                              </div>
                            )}
                          </div>
                          
                          {/* Step labels with help icon */}
                          <div className="flex items-center gap-0.5 mt-1.5">
                            <span
                              className={`text-[10px] text-center max-w-[110px] transition-all leading-tight ${
                                isCurrent 
                                  ? 'text-[#FF6E4C]'
                                  : isLocked
                                  ? 'text-[#717182]'
                                  : 'text-[#18365E]'
                              }`}
                              style={{ fontWeight: isCurrent ? 700 : 600 }}
                            >
                              {step.label}
                            </span>
                            {/* Help icon - hidden on small screens, stops propagation to prevent step click */}
                            <span 
                              className="hidden sm:inline-flex items-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <HelpPopover helpKey={step.helpKey} />
                            </span>
                          </div>
                          
                          {/* Sublabel */}
                          <span
                            className={`text-[9px] text-center transition-all leading-tight ${
                              isLocked ? 'text-[#717182]/60' : 'text-[#717182]'
                            }`}
                            style={{ fontWeight: 500 }}
                          >
                            {step.sublabel}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent 
                        side="bottom" 
                        className={`${
                          isLocked 
                            ? 'bg-[#717182]' 
                            : isCurrent 
                            ? 'bg-[#FF6E4C]' 
                            : 'bg-[#18365E]'
                        } text-white border-0 max-w-xs`}
                      >
                        <p className="text-xs font-medium">
                          {isLocked 
                            ? '🔒 Upgrade to Pro to access this step' 
                            : isLimitedForFreemium
                            ? `${EA_STEP_DESCRIPTIONS[step.descriptionKey].thesis} (${freemiumExplorerLimit} use cases in free tier)`
                            : EA_STEP_DESCRIPTIONS[step.descriptionKey].thesis
                          }
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    
                    {/* Connector line */}
                    {index < eaSteps.length - 1 && (
                      <div
                        className={`h-0.5 flex-1 mx-2 mt-[-36px] transition-all duration-500 ${
                          isCompleted
                            ? 'bg-[#18365E]'
                            : 'bg-[#e5e7eb]'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </TooltipProvider>
          </div>
        </div>
        
        {/* ROW B: Journey Context Row. Storefront/active-step → the step's description; a
            neutral bar (Enterprise tab) → its own one-liner (neutralDescription), or nothing. */}
        {!noActiveStep ? (
        <div className="px-6 py-2 border-t border-[#e5e7eb] bg-[#FAFBFC]">
          <div className="flex items-start justify-between gap-4">
            {/* Left: Step Description (2 lines) */}
            <div className="flex-1 max-w-3xl">
              <p className="text-sm font-semibold text-[#18365E] leading-snug">
                {currentDescription.thesis}
              </p>
              <p className="text-xs text-[#717182] leading-snug mt-0.5">
                {currentDescription.explanation}
              </p>
            </div>

            {/* Right: Reinforcement text (hidden on small screens) */}
            <div className="hidden md:block text-right shrink-0 max-w-[200px]">
              <p className="text-xs text-[#717182] leading-snug">
                {currentReinforcement}
              </p>
            </div>
          </div>
        </div>
        ) : neutralDescription ? (
        <div className="px-6 py-2 border-t border-[#e5e7eb] bg-[#FAFBFC]">
          <div className="flex items-start justify-between gap-4">
            {/* Left: two-part description (matches the storefront step description) */}
            <div className="flex-1 max-w-3xl">
              <p className="text-sm font-semibold text-[#18365E] leading-snug">
                {neutralDescription.thesis}
              </p>
              <p className="text-xs text-[#717182] leading-snug mt-0.5">
                {neutralDescription.description}
              </p>
            </div>

            {/* Right: short forward-looking note (same slot as the storefront reinforcement) */}
            {neutralDescription.rightNote && (
              <div className="hidden md:block text-right shrink-0 max-w-[200px]">
                <p className="text-xs text-[#717182] leading-snug">
                  {neutralDescription.rightNote}
                </p>
              </div>
            )}
          </div>
        </div>
        ) : null}
      </div>

      {/* Personalization Widget - Right Side */}
      <div className="hidden lg:block shrink-0 w-[220px]">
        <EAPersonalizationWidget
          percent={personalizationPct}
          onClick={() => navigate('/home')}
        />
      </div>
    </div>
  );
}

export default EAJourneyBar;